import { useCallback, useEffect, useState } from 'react';
import type { LeituraDTO, UsuarioDTO } from '@medicov1/shared';
import { podeEscrever } from '@medicov1/shared';
import { chamar, ErroApi } from './api';
import { useColeta, type AparelhoCadastrado } from './dados/useColeta';
import { Entrar } from './telas/Entrar';
import { TrocarSenha } from './telas/TrocarSenha';
import { CriarSenha } from './telas/CriarSenha';
import { ConferirMedicacao } from './telas/ConferirMedicacao';
import { vigiarVersao, atualizarAgora } from './versaoNova';
import { Monitor } from './telas/Monitor';
import { Aparelhos } from './telas/Aparelhos';
import { Registrar } from './telas/Registrar';
import { LinhaDoTempo } from './telas/LinhaDoTempo';
import { Anotar } from './telas/Anotar';
import { Ficha } from './telas/Ficha';
import { Plantao, type PlantaoDTO } from './telas/Plantao';
import { Importar } from './telas/Importar';
import { Relatorio } from './telas/Relatorio';
import { Evolucao } from './telas/Evolucao';
import { Perguntas } from './telas/Perguntas';
import { RemediosDoDia } from './telas/RemediosDoDia';
import type { ResumoRemedios } from './telas/Monitor';
import type { ResumoRoteiroDTO } from '@medicov1/shared';

type Tela =
  | 'monitor' | 'aparelhos' | 'registrar' | 'tempo'
  | 'anotar' | 'ficha' | 'plantao' | 'importar' | 'relatorio' | 'conferir' | 'evolucao' | 'perguntas' | 'remedios';

const AMBIENTE = import.meta.env.VITE_AMBIENTE ?? 'desenvolvimento';
const INTERVALO_ATUALIZAR_MS = 15_000;

export interface Paciente {
  id: string;
  apelido: string;
  anoNascimento: number;
  diasInternada: number;
  /** Onde ela esta: "417". Vem do banco; o codigo nao sabe numero de quarto. */
  quarto?: string | null;
}

export function App() {
  const [usuario, setUsuario] = useState<UsuarioDTO | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [temVersaoNova, setTemVersaoNova] = useState(false);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [leituras, setLeituras] = useState<LeituraDTO[]>([]);
  const [erro, setErro] = useState('');
  const [tela, setTela] = useState<Tela>('monitor');
  const [aparelhos, setAparelhos] = useState<AparelhoCadastrado[]>([]);
  const [plantao, setPlantao] = useState<PlantaoDTO | null>(null);
  /**
   * A observacao de hoje ja foi feita?
   *
   * O valor da observacao diaria esta em ser DIARIA: um dia solto nao diz
   * nada. Por isso o monitor lembra enquanto falta — e para de lembrar assim
   * que e feita, que e a unica forma de o lembrete nao virar paisagem.
   */
  const [observouHoje, setObservouHoje] = useState(true);
  /** O roteiro de perguntas da proxima visita, se houver: vira botao no monitor. */
  const [roteiroAtivo, setRoteiroAtivo] = useState<ResumoRoteiroDTO | null>(null);

  const buscarLeituras = useCallback(async (pacienteId: string) => {
    try {
      setLeituras(await chamar<LeituraDTO[]>(`/leituras/ultimas?pacienteId=${pacienteId}`));
      setErro('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'sem contato com o servidor');
    }
  }, []);

  // O plantao muda tres vezes ao dia; segue o mesmo ciclo das leituras porque
  // uma chamada a mais a cada 15 s nao custa nada e evita tela desencontrada.
  const buscarPlantao = useCallback(async (pacienteId: string) => {
    try {
      setPlantao(await chamar<PlantaoDTO | null>(`/plantoes/atual?pacienteId=${pacienteId}`));
    } catch {
      /* sem plantao na tela e melhor do que erro atravessado no monitor */
    }
  }, []);

  // Medida nova chegou pelo Bluetooth: busca de novo em vez de esperar o relogio.
  const aoGravar = useCallback(() => {
    if (paciente) void buscarLeituras(paciente.id);
  }, [paciente, buscarLeituras]);

  const coleta = useColeta(paciente?.id ?? null, aparelhos, aoGravar);

  // O que se publica aqui e correcao de dado clinico. Deixar para quando a
  // pessoa lembrar de recarregar nao serve — ela nao tem motivo para lembrar.
  useEffect(() => vigiarVersao(() => setTemVersaoNova(true)), []);

  useEffect(() => {
    chamar<UsuarioDTO>('/sessao')
      .then(setUsuario)
      .catch((e) => {
        // 401 aqui e o caminho normal de quem ainda nao entrou.
        if (!(e instanceof ErroApi && e.statusCode === 401)) console.error(e);
      })
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    // Com senha provisoria a API recusa tudo: buscar aqui so encheria o console
    // de 403 e piscaria erro numa tela que a pessoa nem deveria ver ainda.
    if (!usuario || usuario.senhaProvisoria) return;
    chamar<Paciente[]>('/pacientes').then((ps) => setPaciente(ps[0] ?? null)).catch(() => {});
    chamar<AparelhoCadastrado[]>('/aparelhos').then(setAparelhos).catch(() => {});
  }, [usuario]);

  const buscarObservacao = useCallback(async (pacienteId: string) => {
    try {
      const r = await chamar<{ deHoje: unknown | null }>(`/observacoes?pacienteId=${pacienteId}&dias=1`);
      setObservouHoje(r.deHoje !== null);
    } catch {
      // Sem resposta, nao se cobra: lembrete aparecendo por causa de rede
      // caida e pior do que lembrete faltando.
      setObservouHoje(true);
    }
  }, []);

  /** Resumo dos remédios de hoje para o card do monitor (atrasados, próximo). */
  const [remedios, setRemedios] = useState<ResumoRemedios | null>(null);
  useEffect(() => {
    if (!paciente || tela !== 'monitor') return;
    const buscar = () => {
      chamar<{ resumo: ResumoRemedios }>(`/medicacoes/dia?pacienteId=${paciente.id}`)
        .then((d) => setRemedios(d.resumo))
        .catch(() => setRemedios(null));
    };
    buscar();
    // O atraso nasce com o relógio, não com um dado novo: de minuto em minuto.
    const t = setInterval(buscar, 60_000);
    return () => clearInterval(t);
  }, [paciente, tela]);

  // O roteiro muda uma vez por visita: busca ao abrir e ao voltar para o monitor.
  useEffect(() => {
    if (!paciente || tela !== 'monitor') return;
    chamar<ResumoRoteiroDTO[]>(`/roteiros?pacienteId=${paciente.id}`)
      .then((l) => setRoteiroAtivo(l.find((r) => r.ativo) ?? null))
      .catch(() => setRoteiroAtivo(null));
  }, [paciente, tela]);

  useEffect(() => {
    if (!paciente) return;
    void buscarLeituras(paciente.id);
    void buscarPlantao(paciente.id);
    void buscarObservacao(paciente.id);
    const t = setInterval(() => {
      void buscarLeituras(paciente.id);
      void buscarPlantao(paciente.id);
      void buscarObservacao(paciente.id);
    }, INTERVALO_ATUALIZAR_MS);
    return () => clearInterval(t);
  }, [paciente, buscarLeituras, buscarPlantao, buscarObservacao]);

  async function sair() {
    await chamar('/sessao', { metodo: 'DELETE' }).catch(() => {});
    setUsuario(null);
    setPaciente(null);
    setLeituras([]);
  }

  return (
    <>
      {AMBIENTE !== 'producao' && <div className="faixa-ambiente">{AMBIENTE} · dados de teste</div>}

      {/* Nao interrompe o que a pessoa esta fazendo: fica ali ate ela querer.
          Recarregar sozinho no meio de uma anotacao seria pior do que a versao
          velha. */}
      {temVersaoNova && (
        <div className="faixa-versao">
          <span>Tem uma versão nova do aplicativo.</span>
          <button onClick={() => void atualizarAgora()}>atualizar agora</button>
        </div>
      )}

      {/* O link de criar senha abre antes de tudo: quem o usa ainda não tem
          senha nenhuma para entrar (30/09). */}
      {window.location.pathname.startsWith('/criar-senha') ? (
        <CriarSenha />
      ) : carregando ? (
        <div className="carregando">carregando…</div>
      ) : !usuario ? (
        <Entrar aoEntrar={setUsuario} />
      ) : usuario.senhaProvisoria ? (
        /* Nao e uma tela a mais no caminho: a API recusa todo o resto enquanto
           a senha entregue pronta nao for trocada. Sem isto, o app carregaria
           vazio e com erro em tudo, sem dizer por que. */
        <TrocarSenha
          nome={usuario.nome}
          aoTrocar={() => setUsuario({ ...usuario, senhaProvisoria: false })}
          aoSair={() => void sair()}
        />
      ) : tela === 'aparelhos' ? (
        <Aparelhos
          status={coleta.status}
          naFila={coleta.naFila}
          suportado={coleta.suportado}
          lembraAparelhos={coleta.lembraAparelhos}
          podeSimular={coleta.podeSimular}
          aoParear={coleta.parear}
          aoDesligar={coleta.desligar}
          aoSimular={coleta.simular}
          aoVoltar={() => setTela('monitor')}
        />
      ) : tela === 'registrar' && paciente ? (
        <Registrar
          pacienteId={paciente.id}
          aoGravar={() => void buscarLeituras(paciente.id)}
          aoVoltar={() => setTela('monitor')}
        />
      ) : tela === 'anotar' && paciente ? (
        <Anotar
          pacienteId={paciente.id}
          aoGravar={() => void buscarLeituras(paciente.id)}
          aoVoltar={() => setTela('monitor')}
        />
      ) : tela === 'plantao' && paciente ? (
        <Plantao
          pacienteId={paciente.id}
          paciente={paciente}
          aoRegistrar={() => void buscarPlantao(paciente.id)}
          aoVoltar={() => setTela('monitor')}
        />
      ) : tela === 'conferir' && paciente ? (
        <ConferirMedicacao pacienteId={paciente.id} aoVoltar={() => setTela('monitor')} />
      ) : tela === 'remedios' && paciente ? (
        <RemediosDoDia pacienteId={paciente.id} perfil={usuario.perfil} usuarioId={usuario.id} aoVoltar={() => setTela('monitor')} />
      ) : tela === 'perguntas' && paciente ? (
        <Perguntas pacienteId={paciente.id} perfil={usuario.perfil} aoVoltar={() => setTela('monitor')} />
      ) : tela === 'relatorio' && paciente ? (
        <Relatorio pacienteId={paciente.id} perfil={usuario.perfil} aoVoltar={() => setTela('monitor')} />
      ) : tela === 'importar' && paciente ? (
        <Importar
          pacienteId={paciente.id}
          aoAplicar={() => void buscarLeituras(paciente.id)}
          aoVoltar={() => setTela('monitor')}
        />
      ) : tela === 'ficha' && paciente ? (
        <Ficha pacienteId={paciente.id} paciente={paciente.apelido} aoVoltar={() => setTela('monitor')} />
      ) : tela === 'tempo' && paciente ? (
        <LinhaDoTempo pacienteId={paciente.id} aoVoltar={() => setTela('monitor')} />
      ) : tela === 'evolucao' && paciente ? (
        <Evolucao
          pacienteId={paciente.id}
          podeRegistrar={podeEscrever(usuario.perfil)}
          aoGravar={() => setObservouHoje(true)}
          aoVoltar={() => setTela('monitor')}
        />
      ) : (
        <Monitor
          usuario={usuario}
          paciente={paciente}
          leituras={leituras}
          erro={erro}
          status={coleta.status}
          naFila={coleta.naFila}
          aoAbrirAparelhos={() => setTela('aparelhos')}
          aoAbrirRegistrar={() => setTela('registrar')}
          aoAbrirTempo={() => setTela('tempo')}
          aoAbrirAnotar={() => setTela('anotar')}
          aoAbrirFicha={() => setTela('ficha')}
          aoAbrirPlantao={() => setTela('plantao')}
          aoAbrirImportar={() => setTela('importar')}
          aoAbrirRelatorio={() => setTela('relatorio')}
          aoAbrirEvolucao={() => setTela('evolucao')}
          faltaObservacaoDeHoje={!observouHoje}
          roteiroAtivo={roteiroAtivo}
          remedios={remedios}
          aoAbrirRemedios={() => setTela('remedios')}
          aoAbrirPerguntas={() => setTela('perguntas')}
          aoConferirMedicacao={() => setTela('conferir')}
          aoGravarLeitura={() => { if (paciente) void buscarLeituras(paciente.id); }}
          plantao={plantao}
          aoSair={sair}
        />
      )}
    </>
  );
}
